import { useCompanyInformation, useCurrentCompany, useProviderTokensAndSyncedProviders } from '@neo.tax/shared/hooks';
import { isTicketingTokenConnected } from '@neo.tax/shared/utils';

interface GettingStartedStatus {
  connectTicketingProviderComplete: boolean;
  companyInfoComplete: boolean;
  canStartAnalysis: boolean;
  uploadedFilesAwaitProcessing: boolean;
  loading: boolean;
}

/**
 * Hook to determine the status of the getting started tasks. This consolidates the
 * business logic for determining whether the getting started tasks are complete.
 */
export const useGettingStartedStatus = ({ fiscalYearId }: { fiscalYearId: string }): GettingStartedStatus => {
  const {
    companyInformation: { elevatorPitch, fiscalYearEndMonth },
    loading: isUseCurrentCompanyLoading,
  } = useCompanyInformation();
  const { loading: isUseProviderTokensAndSyncedProvidersLoading, ticketingTokens } =
    useProviderTokensAndSyncedProviders({ fiscalYearId });
  const { company, loading: isCompanyLoading } = useCurrentCompany();

  // A GitHub App row is armed PENDING before the user ever reaches GitHub, so its mere existence
  // does not mean the provider is connected.
  const hasConnectedTicketingToken = Boolean(ticketingTokens?.some(isTicketingTokenConnected));
  const hasValidTicketingFileUpload = Boolean(
    company?.bulkTicketingDataUploads.some((upload) => upload.isValid === true),
  );

  const connectTicketingProviderComplete = hasConnectedTicketingToken || hasValidTicketingFileUpload;
  // Start Analysis runs on a connected token, so uploaded files alone are left for our team to sync.
  const uploadedFilesAwaitProcessing = hasValidTicketingFileUpload && !hasConnectedTicketingToken;
  const companyInfoComplete = Boolean(elevatorPitch?.length) && fiscalYearEndMonth !== null;
  const canStartAnalysis = hasConnectedTicketingToken && companyInfoComplete;
  const loading = isUseCurrentCompanyLoading || isUseProviderTokensAndSyncedProvidersLoading || isCompanyLoading;

  return {
    connectTicketingProviderComplete,
    companyInfoComplete,
    canStartAnalysis,
    uploadedFilesAwaitProcessing,
    loading,
  };
};
